import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/translationService';

export default function LoyaltyProgram() {
  const [activeTier, setActiveTier] = useState('silver');
  const { language } = useLanguage();
  const [translatedContent, setTranslatedContent] = useState({});
  const [translating, setTranslating] = useState(false);

  // Contenu original en anglais
  const originalContent = {
    hero: {
      title: "Loyalty Program",
      subtitle: "Your loyalty deserves to be rewarded. Discover exclusive benefits with every purchase.",
      button: "Become a Member"
    },
    benefits: {
      title: "Why Join Us?",
      subtitle: "Concrete benefits that make the difference",
      items: [
        {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          ),
          title: "Loyalty Points",
          description: "Earn points with every purchase and exchange them for rewards."
        },
        {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          ),
          title: "Exclusive Benefits",
          description: "Access special offers and previews of new products."
        },
        {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
            </svg>
          ),
          title: "Birthday Gift",
          description: "Receive a special gift and discount for your birthday."
        }
      ]
    },
    tiers: {
      title: "Our Membership Levels",
      subtitle: "Progress and unlock exclusive benefits",
      levels: [
        {
          id: 'member',
          name: 'Member',
          points: 'Free',
          features: [
            '1 point per euro spent',
            'Exclusive newsletter',
            'Access to sales',
            'Digital beauty guide'
          ],
          button: 'Join Free'
        },
        {
          id: 'silver',
          name: 'Silver',
          points: '500 points',
          features: [
            '1.5 points per euro',
            'Free shipping from €50',
            'Free samples',
            'Product preview access',
            'Priority support'
          ],
          button: 'Upgrade Level'
        },
        {
          id: 'gold',
          name: 'Gold',
          points: '1500 points',
          features: [
            '2 points per euro',
            'Free returns',
            'Exclusive gifts',
            'Personalized advice',
            'Early access to new products',
            'Quarterly discovery box'
          ],
          button: 'Upgrade Level'
        }
      ]
    },
    cta: {
      title: "Ready to Start?",
      subtitle: "Join our loyalty program and start earning rewards today.",
      buttons: {
        create: "Create Account",
        learn: "Learn More"
      },
      login: "Already a member?",
      loginLink: "Log in"
    }
  };

  // Traduction du contenu
  useEffect(() => {
    const translateContent = async () => {
      if (language === 'en') {
        setTranslatedContent(originalContent);
        return;
      }

      try {
        setTranslating(true);
        console.log(`🌐 Translating Loyalty Program: en → ${language}`);

        const translated = { ...originalContent };

        // Traduire le contenu principal
        for (const [sectionKey, sectionValue] of Object.entries(originalContent)) {
          if (typeof sectionValue === 'object') {
            for (const [key, value] of Object.entries(sectionValue)) {
              if (typeof value === 'string') {
                translated[sectionKey][key] = await translateText(value, language, 'en');
              } else if (Array.isArray(value)) {
                // Traduire les tableaux (benefits, tiers)
                if (sectionKey === 'benefits' && key === 'items') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(async (item) => ({
                      ...item,
                      title: await translateText(item.title, language, 'en'),
                      description: await translateText(item.description, language, 'en')
                    }))
                  );
                } else if (sectionKey === 'tiers' && key === 'levels') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(async (tier) => ({
                      ...tier,
                      name: await translateText(tier.name, language, 'en'),
                      points: await translateText(tier.points, language, 'en'),
                      features: await Promise.all(
                        tier.features.map(feature => 
                          translateText(feature, language, 'en')
                        )
                      ),
                      button: await translateText(tier.button, language, 'en')
                    }))
                  );
                }
              } else if (typeof value === 'object') {
                // Traduire les objets imbriqués (buttons)
                for (const [subKey, subValue] of Object.entries(value)) {
                  if (typeof subValue === 'string') {
                    translated[sectionKey][key][subKey] = await translateText(subValue, language, 'en');
                  }
                }
              }
            }
          }
        }

        setTranslatedContent(translated);
        console.log('✅ Loyalty Program translated successfully');
      } catch (error) {
        console.error('💥 Translation error Loyalty Program:', error);
        setTranslatedContent(originalContent);
      } finally {
        setTranslating(false);
      }
    };

    translateContent();
  }, [language]);

  // Utiliser le contenu traduit ou original
  const content = translatedContent.hero ? translatedContent : originalContent;

  // Fonction pour gérer l'inscription
  const handleJoin = () => {
    // Rediriger vers la page d'inscription
    window.location.href = 'http://localhost:3000/register';
  };

  const handleCreateAccount = () => {
    // Rediriger vers la page d'inscription
    window.location.href = 'http://localhost:3000/register';
  };

  const handleLearnMore = () => {
    // Scroll vers la section des avantages
    document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Overlay de traduction */}
      {translating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {language === 'fr' ? 'Mise à jour des traductions...' : 
               language === 'ar' ? 'جاري تحديث الترجمات...' : 
               'Updating translations...'}
            </p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-light mb-6">{content.hero?.title || "Loyalty Program"}</h1>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            {content.hero?.subtitle || "Your loyalty deserves to be rewarded. Discover exclusive benefits with every purchase."}
          </p>
          <button 
            onClick={handleJoin}
            className="bg-[#d0b125] text-white px-8 py-3 rounded-lg hover:bg-[#b89a20] transition-colors"
          >
            {content.hero?.button || "Become a Member"}
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              {content.benefits?.title || "Why Join Us?"}
            </h2>
            <p className="text-gray-600">
              {content.benefits?.subtitle || "Concrete benefits that make the difference"}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.benefits?.items?.map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              {content.tiers?.title || "Our Membership Levels"}
            </h2>
            <p className="text-gray-600">
              {content.tiers?.subtitle || "Progress and unlock exclusive benefits"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.tiers?.levels?.map(tier => (
              <div 
                key={tier.id}
                className={`bg-white rounded-lg border-2 transition-all ${
                  activeTier === tier.id ? 'border-[#d0b125] shadow-lg' : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-2xl font-light text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-[#d0b125] font-medium">{tier.points}</div>
                </div>
                
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 text-amber-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => setActiveTier(tier.id)}
                    className={`w-full py-3 rounded-lg transition-colors ${
                      activeTier === tier.id 
                        ? 'bg-[#d0b125] text-white hover:bg-[#b89a20]' 
                        : 'bg-gray-900 text-white hover:bg-[#d0b125]'
                    }`}
                  >
                    {tier.button}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            {content.cta?.title || "Ready to Start?"}
          </h2>
          <p className="text-gray-600 mb-8">
            {content.cta?.subtitle || "Join our loyalty program and start earning rewards today."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleCreateAccount}
              className="bg-[#d0b125] text-white px-8 py-3 rounded-lg hover:bg-[#b89a20] transition-colors"
            >
              {content.cta?.buttons?.create || "Create Account"}
            </button>
            <button 
              onClick={handleLearnMore}
              className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:border-amber-300 transition-colors"
            >
              {content.cta?.buttons?.learn || "Learn More"}
            </button>
          </div>
          <p className="text-gray-600 mt-6">
            {content.cta?.login || "Already a member?"}{' '}
            <Link 
              to="http://localhost:3000/login" 
              className="text-[#d0b125] hover:text-[#b89a20]"
            >
              {content.cta?.loginLink || "Log in"}
            </Link>
          </p>
        </div>
      </section>

      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded z-50">
          Language: {language}
        </div>
      )}
    </div>
  );
}